<template>
  <div class="status-summary">
    <div class="status-symbols">
      <a v-if="hasCertificate" :href="certPath" target="_blank" :class="certClasses">
        <b-icon icon="file-pdf" size="is-medium" :title="certTitle" />
      </a>
      <span
        v-if="downloadable"
        class="trigger generate"
        @click.prevent="generate"
        :class="certClasses"
      >
        <b-icon icon="file-pdf" size="is-medium" :title="certTitle" />
      </span>
      <b-icon
        v-if="shortlisted"
        icon="shield-check-outline"
        size="is-medium"
        title="Return to later"
      />
      <b-icon
        v-if="hasSelectedWork"
        icon="license"
        size="is-medium"
        title="Has shortlisted work(s)"
      />
    </div>
    <b-field class="payment-details">
      <b-checkbox size="is-small" v-model="feePaid">Fee paid</b-checkbox>
      <div v-if="hasTransactionRef" class="transaction-ref">{{transactionRef}}</div>
    </b-field>
    <b-field>
      <b-checkbox size="is-small" v-model="submitted">submitted</b-checkbox>
    </b-field>
    <b-field>
      <b-checkbox size="is-small" v-model="ineligible">ineligible</b-checkbox>
    </b-field>
    <b-field>
      <b-checkbox size="is-small" v-model="longRead">long read</b-checkbox>
    </b-field>
    <b-field>
      <b-checkbox size="is-small" v-model="marked">queried</b-checkbox>
    </b-field>
    <b-field>
      <b-checkbox size="is-small" v-model="checked">checked</b-checkbox>
    </b-field>
    <b-field>
      <b-checkbox size="is-small" v-model="deadlineException">Deadline exception</b-checkbox>
    </b-field>
    <b-field>
      <b-checkbox size="is-small" v-model="shortlisted" :disabled="shortlistedDisabled">return to</b-checkbox>
    </b-field>
    <b-field>
      <b-checkbox size="is-small" v-model="signUp">Signed up</b-checkbox>
    </b-field>
    <b-field label="Notes">
      <b-input maxlength="200" type="textarea" v-model="notes"></b-input>
    </b-field>
    <p class="datetime created">
      <span class="text">Created at:</span>
      <time>{{createdAt|shortDatetime}}</time>
    </p>
    <p class="datetime modified">
      <span class="text">Updated:</span>
      <time>{{modifiedAt|shortDatetime}}</time>
    </p>
  </div>
</template>
<script type="text/javascript">
import { bus } from "../main";
import filters from "../mixins/filters";
import { saveSubmission, matchPdfPath, generatePdf, updateUser } from "../api/methods";
import { extractString, extractBool, extractSeq } from "../api/helpers";
import { mapSubmission, matchDisabledSubmissionStatuses } from "../api/mappers";
import { submissionSchema } from "../store/submission";

export default {
  name: "StatusSummary",
  props: {
    submission: {
      type: Object,
      required: true,
      default: submissionSchema
    }
  },
  data() {
    return {
      notes: "",
      submitted: false,
      longRead: false,
      checked: false,
      marked: false,
      shortlisted: false,
      ineligible: false,
      returnTo: false,
      deadlineException: false,
      feePaid: true,
      signUp: false,
      loading: false,
      certificate: {
        path: "",
        valid: false,
        modified: null
      }
    };
  },
  mixins: [filters],
  mounted() {
    this.signUp = false;
    this.submitted = extractBool(this.submission, "submitted");
    this.checked = extractBool(this.submission, "checked");
    this.marked = extractBool(this.submission, "marked");
    this.ineligible = extractBool(this.submission, "ineligible");
    this.shortlisted =
      extractBool(this.submission, "shortlisted") ||
      extractBool(this.submission, "hasSelectedWork");
    this.longRead = extractBool(this.submission, "longRead");
    this.returnTo = extractBool(this.submission, "returnTo");
    this.feePaid = extractBool(this.submission, "feePaid");
    this.deadlineException = extractBool(this.submission, "deadlineException");
    this.notes = extractString(this.submission, "notes");
    if (this.submission.user instanceof Object) {
      this.signUp = extractBool(this.submission.user, "signUp");
    }
    bus.$on("save-details", this.triggerSave);
    this.loaded = false;
    setTimeout(() => {
      this.loaded = true;
    }, 500);
    setTimeout(this.checkCertificate, 1000);
  },
  beforeDestroy() {
    bus.$off("save-details", this.triggerSave);
  },
  watch: {
    submitted(cv, pv) {
      this.saveChanged(cv, pv, "submitted");
    },
    longRead(cv, pv) {
      this.saveChanged(cv, pv, "longRead");
    },
    checked(cv, pv) {
      this.saveChanged(cv, pv, "checked");
    },
    marked(cv, pv) {
      this.saveChanged(cv, pv, "marked");
    },
    ineligible(cv, pv) {
      this.saveChanged(cv, pv, "ineligible");
    },
    returnTo(cv, pv) {
      this.saveChanged(cv, pv, "returnTo");
    },
    shortlisted(cv, pv) {
      this.saveChanged(cv, pv, "shortlisted");
    },
    feePaid(cv, pv) {
      this.saveChanged(cv, pv, "feePaid");
    },
    signUp(cv, pv) {
      this.saveChanged(cv, pv, "signUp");
    },
    deadlineException(cv, pv) {
      this.saveChanged(cv, pv, "deadlineException");
    }
  },
  computed: {
    hasSelectedWork() {
      return this.submission.hasSelectedWork;
    },
    createdAt() {
      return this.submission.createdAt;
    },
    modifiedAt() {
      return this.submission.modifiedAt;
    },
    shortlistedDisabled() {
      return this.hasSelectedWork && this.submission.shortlisted;
    },
    transactionRef() {
      return extractString(this.submission, "transactionRef");
    },
    hasTransactionRef() {
      return this.transactionRef.length > 2;
    },
    hasNotes() {
      return this.notes.length > 1;
    },
    hasCertificate() {
      return this.certificate.valid;
    },
    downloadable() {
      return this.submitted && !this.hasCertificate;
    },
    certPath() {
      return this.hasCertificate ? this.certificate.path : "#";
    },
    certTitle() {
      return this.hasCertificate
        ? "Generated on " + this.shortDatetime(this.certificate.modified)
        : "Click to generate";
    },
    certClasses() {
      let cls = [];
      if (!this.hasCertificate) {
        cls.push("inactive");
      }
      if (this.downloadable) {
        cls.push("downloadable");
      }
      return cls;
    }
  },
  methods: {
    saveAttribute(varName = '', cv = '') {
      const edited = {
        _id: this.submission._id,
        user: this.submission.user._id,
        [varName]: cv
      };
      if (cv) {
        let disableItems = matchDisabledSubmissionStatuses(varName);
        if (disableItems.length > 0) {
          disableItems.forEach(key => {
            edited[key] = false;
            this[key] = false;
          });
        }
        if (varName === "deadlineException") {
          edited.submitted = false;
          this.submitted = false;
        }
      }
      saveSubmission(edited).then(data => {
        if (data.valid) {
          if (this.$parent.$parent.items instanceof Array) {
            const parSubIndex = this.$parent.$parent.items.findIndex(
              s => s._id === data.submission._id
            );
            if (parSubIndex >= 0) {
              let sub = data.submission;
              const currSub = this.$parent.$parent.items[parSubIndex];
              sub.user = currSub.user;
              sub.uid = currSub.uid;
              this.$parent.$parent.items[parSubIndex] = mapSubmission(sub);
              if (varName === "shortlisted" && sub.works.length > 0) {
                bus.$emit("work-updated", sub.works[0], "shortlisted", cv);
              }
              bus.$emit("update-status", { subId: sub._id, key: varName, boolVal: cv });
              this.$buefy.toast.open({
                duration: 1500,
                message: `Submission ${sub.uid} updated`,
                position: "is-bottom",
                type: "is-success"
              });
            }
          }
        }
      });
    },
    saveChanged(cv, pv, varName) {
      if (this.loaded && pv !== cv) {
        if (this.submission.user) {
          if (varName === 'signUp') {
            const signUp = cv === true;
            updateUser(this.submission.user._id, { signUp }).then(result => {
              if (result instanceof Object) {
                const {user} = result;
                if (user instanceof Object) {
                  const ml = `the mailing list`;
                  if (user.firstName) {
                    const fullName = [user.firstName, user.lastName].join(' ');
                    const message = signUp ? `Added ${fullName} to ${ml}` : `Removed ${fullName} from ${ml}`
                    this.$buefy.toast.open({
                      duration: 2000,
                      message,
                      position: "is-bottom",
                      type: "is-success"
                    });
                    bus.$emit("update-status", { subId: this.submission._id, key: varName, boolVal: cv });
                  }
                }
              }
            });
          } else {
            this.saveAttribute(varName, cv);
          }
        }
      }
    },
    triggerSave() {
      setTimeout(() => {
        this.saveChanged(this.notes, "", "notes");
      }, 500);
    },
    checkCertificate() {
      if (this.submission) {
        const { user } = this.submission;
        if (user instanceof Object) {
          matchPdfPath(user._id).then(data => {
            if (data.valid) {
              this.certificate = data;
            }
          });
        }
      }
    },
    generate() {
      if (this.submission) {
        const { user, _id } = this.submission;
        if (user instanceof Object) {
          generatePdf(_id, user._id).then(data => {
            if (data.valid) {
              this.certificate.path =
                "/pdfs/application-" + extractSeq(user) + ".pdf";
              this.certificate.modified = new Date().toISOString();
              this.certificate.valid = true;
            }
          });
        }
      }
    }
  }
};
</script>